import { formWithValidation } from '@edgebox/react-components';
import { AppParamsContext, IAppParams } from '../../AppParams';
import { onboardingContinueLabel, onboardingDomainCell, onboardingStatusCell } from './Components/onboardingContinueLabel.utils';
import { formatDate } from './Components/brandPresenceDates';
import { listExistingOnboardingTaskGroups } from './Components/existingOnboardings';
import { useResumableOnboardings } from './Components/useResumableOnboardings';
import { InternalId, StaticReference, Uuid } from '@edgebox/data-definition-kit';
import {
  TaskDomain,
  TaskGroupType,
  TaskOperationSubType,
  TaskOperationType,
  TaskStatus,
  TaskType,
} from '@edgebox/sync-core-data-definitions';
import { ClientTaskGroupDraft, ClientTaskGroupEntity, ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import { Transform, instanceToPlain, plainToInstance } from 'class-transformer';
import React from 'react';
import { IsString, Matches, MaxLength } from 'class-validator';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faGlobe } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router';
import { Field } from 'formik';
import type { FieldProps } from 'formik';
import { resolveBrandPresenceOnboardingSite, sendToParent } from '../../../frame-messages';
import { BrandPresenceEyebrow } from './Components/BrandPresencePageHeader';
import { SkeletonBlock, SkeletonRegion } from './Components/BrandPresenceSkeleton';
import { GovernanceOnboardingContainer } from './Onboarding/components/GovernanceOnboardingPrimitives';
import { SyncCoreApiContext } from '../../../contexts/SyncCoreApiContext';
import { isTerminalClientError } from './Components/clientErrorTerminal';

function normalizeDomainInput(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return trimmed;
  }

  const withProtocol = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

  try {
    const parsed = new URL(withProtocol);
    const host = parsed.hostname.trim().toLowerCase();
    return host.endsWith('.') ? host.slice(0, -1) : host;
  } catch {
    return trimmed;
  }
}

export interface IGovernanceOnboardingData {
  domain: string;
}

interface IResolvedOnboardingSite {
  id: string;
  uuid: string;
  baseUrl: string;
  name: string;
}

export class GovernanceOnboardingData implements IGovernanceOnboardingData {
  @IsString()
  @MaxLength(255)
  @Transform(({ value }) => normalizeDomainInput(String(value ?? '')))
  @Matches(/^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/, { message: 'Invalid domain name' })
  domain!: string;
}

const GovernanceOnboardingForm = formWithValidation<GovernanceOnboardingData, GovernanceOnboardingData>(
  (values) => plainToInstance(GovernanceOnboardingData, values),
  (entity) => instanceToPlain(entity) as GovernanceOnboardingData
);

async function sleep(ms: number) {
  await new Promise((resolve) => setTimeout(resolve, ms));
}

enum OnboardingProgressStep {
  ResolvingSite = 'resolving-site',
  SettingUpSpace = 'setting-up-space',
  CreatingOnboardingTask = 'creating-onboarding-task',
  InitializingOnboarding = 'initializing-onboarding',
  VisitHomePage = 'visit-home-page',
  ExtractContentRetrieve = 'extract-content-retrieve',
  ExtractTerms = 'extract-terms',
  ExtractContentExtract = 'extract-content-extract',
  SelectLinks = 'select-links',
  Done = 'done',
}

const ONBOARDING_STEP_PROGRESS: Record<OnboardingProgressStep, number> = {
  [OnboardingProgressStep.ResolvingSite]: 3,
  [OnboardingProgressStep.SettingUpSpace]: 6,
  [OnboardingProgressStep.CreatingOnboardingTask]: 9,
  [OnboardingProgressStep.InitializingOnboarding]: 12,
  [OnboardingProgressStep.VisitHomePage]: 20,
  [OnboardingProgressStep.ExtractContentRetrieve]: 31,
  [OnboardingProgressStep.ExtractTerms]: 74,
  [OnboardingProgressStep.ExtractContentExtract]: 87,
  [OnboardingProgressStep.SelectLinks]: 94,
  [OnboardingProgressStep.Done]: 100,
};

const ONBOARDING_STEP_MESSAGE: Record<OnboardingProgressStep, string> = {
  [OnboardingProgressStep.ResolvingSite]: 'Resolving site…',
  [OnboardingProgressStep.SettingUpSpace]: 'Setting up space…',
  [OnboardingProgressStep.CreatingOnboardingTask]: 'Creating onboarding task…',
  [OnboardingProgressStep.InitializingOnboarding]: 'Initializing onboarding…',
  [OnboardingProgressStep.VisitHomePage]: 'Visit home page…',
  [OnboardingProgressStep.ExtractContentRetrieve]: 'Extract content…',
  [OnboardingProgressStep.ExtractTerms]: 'Extract terms…',
  [OnboardingProgressStep.ExtractContentExtract]: 'Summarizing…',
  [OnboardingProgressStep.SelectLinks]: 'Select links…',
  [OnboardingProgressStep.Done]: 'All done!',
};

function getLatestOperation(
  task: { operations?: Array<{ type?: TaskOperationType; subType?: TaskOperationSubType; status?: TaskStatus }> },
  type: TaskOperationType
) {
  return [...(task.operations || [])].reverse().find((operation) => operation.type === type);
}

async function resolveActiveOnboardingProgressStep(api: ISyncCoreApi, taskGroupId: InternalId): Promise<OnboardingProgressStep> {
  const tasksResult = await api.service.tasks.list(
    {
      domains: [TaskDomain.Governance],
      migrationId: taskGroupId,
      types: [TaskType.Onboarding],
    },
    {
      page: 0,
      itemsPerPage: 1,
    }
  );

  const onboardingTask = tasksResult.items[0];
  if (!onboardingTask) {
    return OnboardingProgressStep.VisitHomePage;
  }

  const selectOperation = [...(onboardingTask.operations || [])].find((operation) => operation.type === TaskOperationType.Select);
  if (selectOperation && selectOperation.status === TaskStatus.Finished) {
    return OnboardingProgressStep.Done;
  }

  const mainPageExtractOperation = [...(onboardingTask.operations || [])].find(
    (operation) => operation.type === TaskOperationType.Extract && operation.subType === TaskOperationSubType.ExtractMainPageData
  );

  if (!mainPageExtractOperation) {
    return OnboardingProgressStep.VisitHomePage;
  }

  if (mainPageExtractOperation.status === TaskStatus.Finished) {
    return OnboardingProgressStep.SelectLinks;
  }

  const syndicationTaskId = mainPageExtractOperation.syndicationIds?.[0] as InternalId | undefined;
  if (!syndicationTaskId) {
    return OnboardingProgressStep.VisitHomePage;
  }

  try {
    const syndicationTask = await api.service.tasks.item(syndicationTaskId, true, { includeUsage: true });
    const extractOperation = getLatestOperation(syndicationTask, TaskOperationType.Extract);
    if (extractOperation?.status === TaskStatus.Finished) {
      return OnboardingProgressStep.ExtractContentExtract;
    }

    const parseOperation = getLatestOperation(syndicationTask, TaskOperationType.Parse);
    if (parseOperation?.status === TaskStatus.Finished) {
      return OnboardingProgressStep.ExtractTerms;
    }

    const retrieveOperation = getLatestOperation(syndicationTask, TaskOperationType.Retrieve);
    if (retrieveOperation?.status === TaskStatus.Finished) {
      return OnboardingProgressStep.ExtractContentRetrieve;
    }
  } catch {
    return OnboardingProgressStep.VisitHomePage;
  }

  return OnboardingProgressStep.VisitHomePage;
}

function getLatestTaskGroup(taskGroups: ClientTaskGroupEntity[]): ClientTaskGroupEntity | undefined {
  if (!taskGroups.length) {
    return undefined;
  }

  return [...taskGroups].sort((a, b) => String(b.id).localeCompare(String(a.id)))[0];
}

async function findLatestOnboardingTaskGroup(api: ISyncCoreApi, siteUuid: Uuid): Promise<ClientTaskGroupEntity | undefined> {
  const taskGroupList = await api.service.taskGroups.list(
    {
      siteUuid,
      domains: [TaskDomain.Governance],
      types: [TaskGroupType.Onboarding],
    },
    {
      page: 0,
      itemsPerPage: 20,
    }
  );

  return getLatestTaskGroup(taskGroupList.items);
}

async function importSiteIntoSyncCore(api: ISyncCoreApi, siteUuid: Uuid): Promise<InternalId> {
  for (let attempt = 0; attempt < 15; attempt += 1) {
    try {
      const importedSite = await api.utility.backend.importSite(siteUuid);
      return importedSite.id as InternalId;
    } catch {
      await sleep(1000);
    }
  }

  throw new Error('Failed to import site into Sync Core. Please try again.');
}

async function initiateOnboardingTaskGroups(api: ISyncCoreApi, siteId: InternalId): Promise<void> {
  const createTaskGroup = async (type: TaskGroupType) => {
    const taskGroupDraft = new ClientTaskGroupDraft();
    taskGroupDraft.type = type;
    Object.assign(taskGroupDraft, {
      site: new StaticReference({ id: siteId }),
    });

    await api.service.taskGroups.create(taskGroupDraft);
  };

  // TaskGroupType.Discover is now created by the backend to handle dependencies,
  // so we're only creating the onboarding group for now.
  await Promise.all([createTaskGroup(TaskGroupType.Onboarding)]);
}

export function GovernanceOnboarding() {
  const navigate = useNavigate();
  const apiContext = React.useContext(SyncCoreApiContext);
  const [lastSubmitted, setLastSubmitted] = React.useState<IGovernanceOnboardingData | undefined>(undefined);
  const [resolving, setResolving] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [currentStep, setCurrentStep] = React.useState<OnboardingProgressStep>(OnboardingProgressStep.ResolvingSite);
  const [progress, setProgress] = React.useState(0);
  // The scope the request needs. When it changes from absent to present, the fetch above re-runs.
  const onboardingAppParams = React.useContext(AppParamsContext) as IAppParams | null;
  const projectUuid = onboardingAppParams?.pageOptions?.projectUuid as string | undefined;
  const [showOnboardingStartScreen, setShowOnboardingStartScreen] = React.useState(false);

  // The fetch, its terminal-4xx handling and its scope keying all live in the hook, where the
  // regression that broke a started onboarding is covered by tests.
  const { onboardings: existingOnboardingTaskGroups, loading: loadingExistingOnboardingTaskGroups } = useResumableOnboardings(
    apiContext?.api,
    projectUuid,
    // Passed directly, with no cast: the hook is generic over the client, so this is where the
    // compiler checks that the real `ISyncCoreApi` satisfies the slice the loader declares it needs.
    listExistingOnboardingTaskGroups,
    (error) => {
      // A 5xx is a real server fault and is deliberately NOT special-cased: it lands in the same
      // empty state, but it is logged so a genuine outage is not silently read as "new customer".
      if (!isTerminalClientError(error)) {
        // eslint-disable-next-line no-console
        console.warn('Could not list existing onboarding task groups', error);
      }
    }
  );

  const hasExistingOnboardingTaskGroups = existingOnboardingTaskGroups.length > 0;

  React.useEffect(() => {
    if (!resolving) {
      setProgress(0);
      return;
    }

    const target = ONBOARDING_STEP_PROGRESS[currentStep];
    const interval = setInterval(() => {
      setProgress((current) => {
        if (current >= target) {
          return current;
        }

        const increment = 1;
        return Math.min(target, current + increment);
      });
    }, 600);

    return () => clearInterval(interval);
  }, [currentStep, resolving]);

  const indicatorSize = 220;
  const strokeWidth = 16;
  const radius = (indicatorSize - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (progress / 100) * circumference;
  const currentStatus = ONBOARDING_STEP_MESSAGE[currentStep];

  return (
    <GovernanceOnboardingContainer>
      <div className="px-8 py-16 md:px-14 md:py-20">
        {resolving ? (
          <div className="mx-auto max-w-2xl text-center" role="status" aria-live="polite">
            <BrandPresenceEyebrow className="justify-center">
              Brand Presence <span className="text-slate-300">/</span> Onboarding
            </BrandPresenceEyebrow>
            <h1 className="mb-4 text-3xl font-bold tracking-tight text-slate-900 [text-wrap:balance] md:text-4xl">
              Analyzing your website
            </h1>
            <p className="mb-10 text-center text-lg leading-relaxed text-slate-500">
              We are visiting your website to learn more about you and your content. This usually takes about a minute to complete.
            </p>

            <div className="mx-auto mb-8" style={{ width: indicatorSize, height: indicatorSize }}>
              <svg width={indicatorSize} height={indicatorSize} viewBox={`0 0 ${indicatorSize} ${indicatorSize}`}>
                <defs>
                  <linearGradient id="onboardingProgressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#009EE3" />
                    <stop offset="34%" stopColor="#7491ED" />
                    <stop offset="68%" stopColor="#D96ECC" />
                    <stop offset="100%" stopColor="#FF5479" />
                  </linearGradient>
                </defs>

                <circle cx={indicatorSize / 2} cy={indicatorSize / 2} r={radius} fill="none" stroke="#E2E8F0" strokeWidth={strokeWidth} />
                <circle
                  cx={indicatorSize / 2}
                  cy={indicatorSize / 2}
                  r={radius}
                  fill="none"
                  stroke="url(#onboardingProgressGradient)"
                  strokeWidth={strokeWidth}
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={dashOffset}
                  transform={`rotate(-90 ${indicatorSize / 2} ${indicatorSize / 2})`}
                />

                <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle" className="fill-slate-900 text-5xl font-bold">
                  {progress}%
                </text>
              </svg>
            </div>

            <p className="text-slate-500 text-lg">{currentStatus}</p>
            {lastSubmitted ? <p className="mt-2 text-sm text-slate-400">{lastSubmitted.domain}</p> : null}
          </div>
        ) : (
          <>
            {loadingExistingOnboardingTaskGroups ? (
              <div className="mx-auto max-w-2xl text-center">
                <BrandPresenceEyebrow className="justify-center">
                  Brand Presence <span className="text-slate-300">/</span> Onboarding
                </BrandPresenceEyebrow>
                <h1 className="mb-4 text-3xl font-bold tracking-tight text-slate-900 [text-wrap:balance] md:text-4xl">
                  Loading onboarding
                </h1>
                <SkeletonRegion label="Checking your existing onboarding projects…" className="mx-auto max-w-md space-y-3 text-left">
                  <SkeletonBlock className="mx-auto h-4 w-72" />
                  <div className="mt-8 space-y-2 rounded-xl p-4 ring-1 ring-inset ring-slate-200/80">
                    {[0, 1, 2].map((row) => (
                      <div key={row} className="flex items-center justify-between gap-4 py-1.5">
                        <SkeletonBlock className={`h-4 ${row % 2 ? 'w-32' : 'w-40'}`} />
                        <SkeletonBlock className="h-4 w-20" />
                        <SkeletonBlock className="hidden h-4 w-20 sm:block" />
                        <SkeletonBlock className="h-4 w-14" />
                        <SkeletonBlock className="h-8 w-24 rounded-lg" />
                      </div>
                    ))}
                  </div>
                </SkeletonRegion>
              </div>
            ) : hasExistingOnboardingTaskGroups && !showOnboardingStartScreen ? (
              <div className="mx-auto w-full max-w-3xl">
                <div className="text-center mb-10">
                  <BrandPresenceEyebrow className="justify-center">
                    Brand Presence <span className="text-slate-300">/</span> Onboarding
                  </BrandPresenceEyebrow>
                  <h1 className="mb-4 text-3xl font-bold tracking-tight text-slate-900 [text-wrap:balance] md:text-4xl">
                    Continue where you left off
                  </h1>
                  <p className="text-center text-lg leading-relaxed text-slate-500">
                    Resume an existing onboarding project or start onboarding a new site when you are ready.
                  </p>
                </div>

                {existingOnboardingTaskGroups.length === 1 ? (
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => {
                        const taskGroupId = existingOnboardingTaskGroups[0].id;
                        sendToParent({
                          type: 'update-path',
                          path: `/brand-presence/onboarding/${encodeURIComponent(taskGroupId)}/key-content`,
                        });
                        navigate(`/brand-presence/onboarding/${encodeURIComponent(taskGroupId)}/key-content`);
                      }}
                      className="rounded-lg px-8 py-3 bg-brand-gradient font-semibold text-white shadow-sm transition-all duration-150 hover:shadow-card-hover hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
                    >
                      {onboardingContinueLabel(existingOnboardingTaskGroups[0], loadingExistingOnboardingTaskGroups)}
                    </button>
                    <div className="mt-4">
                      <button
                        type="button"
                        onClick={() => setShowOnboardingStartScreen(true)}
                        className="text-sm font-semibold text-primary hover:underline"
                      >
                        Onboard a new site
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-5">
                    {/* A table wider than the screen scrolls sideways in its own frame, rather than
                        being cut off at the page's edge. */}
                    <div className="overflow-x-auto rounded-xl ring-1 ring-inset ring-slate-200/80">
                      <table className="w-full text-left">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-4 py-3 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-slate-500">Domain</th>
                            <th className="px-4 py-3 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-slate-500">Started</th>
                            <th className="hidden px-4 py-3 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-slate-500 sm:table-cell">
                              Updated
                            </th>
                            <th className="px-4 py-3 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-slate-500">Status</th>
                            <th className="px-4 py-3 text-right text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-slate-500">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {existingOnboardingTaskGroups.map((taskGroup) => (
                            <tr
                              key={taskGroup.id}
                              className="border-t border-slate-100 transition-colors duration-150 hover:bg-slate-50/80"
                            >
                              <td className="px-4 py-3 text-sm text-slate-900">{onboardingDomainCell(taskGroup)}</td>
                              <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-700">{formatDate(taskGroup.createdAt)}</td>
                              <td className="hidden whitespace-nowrap px-4 py-3 text-sm text-slate-700 sm:table-cell">
                                {formatDate(taskGroup.updatedAt)}
                              </td>
                              <td className="px-4 py-3 text-sm text-slate-700">{onboardingStatusCell(taskGroup.status)}</td>
                              <td className="px-4 py-3 text-right">
                                <button
                                  type="button"
                                  onClick={() => {
                                    sendToParent({
                                      type: 'update-path',
                                      path: `/brand-presence/onboarding/${encodeURIComponent(taskGroup.id)}/key-content`,
                                    });
                                    navigate(`/brand-presence/onboarding/${encodeURIComponent(taskGroup.id)}/key-content`);
                                  }}
                                  className="inline-flex h-9 items-center rounded-lg bg-primary/10 px-4 text-sm font-semibold text-primary ring-1 ring-inset ring-primary/20 transition-colors duration-150 hover:bg-primary/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                                >
                                  Continue
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="text-center">
                      <button
                        type="button"
                        onClick={() => setShowOnboardingStartScreen(true)}
                        className="text-sm font-semibold text-primary hover:underline"
                      >
                        Onboard a new site
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="mx-auto max-w-2xl text-center">
                  {hasExistingOnboardingTaskGroups ? (
                    <div className="mb-6 text-left">
                      <button
                        type="button"
                        onClick={() => setShowOnboardingStartScreen(false)}
                        className="text-sm font-semibold text-slate-500 hover:text-slate-700 transition-colors inline-flex items-center gap-2"
                      >
                        <FontAwesomeIcon icon={faArrowLeft} />
                        <span>Back</span>
                      </button>
                    </div>
                  ) : null}

                  <BrandPresenceEyebrow className="justify-center">
                    Brand Presence <span className="text-slate-300">/</span> Onboarding
                  </BrandPresenceEyebrow>
                  <h1 className="mb-4 text-3xl font-bold tracking-tight text-slate-900 [text-wrap:balance] md:text-4xl">
                    Start your Brand Presence journey
                  </h1>
                  <p className="mb-10 text-center text-lg leading-relaxed text-slate-500">
                    Enter your primary domain to analyze your visibility across AI, search, and digital channels.
                  </p>
                </div>

                <div className="mx-auto w-full max-w-2xl">
                  <GovernanceOnboardingForm
                    initialValues={plainToInstance(GovernanceOnboardingData, { domain: '' })}
                    onSubmit={async (values) => {
                      if (!apiContext?.api) {
                        setError('Sync Core API context is missing.');
                        return;
                      }

                      const normalizedDomain = normalizeDomainInput(values.domain);

                      setResolving(true);
                      setError(undefined);
                      setCurrentStep(OnboardingProgressStep.ResolvingSite);

                      try {
                        setLastSubmitted({ domain: normalizedDomain });

                        const site = (await resolveBrandPresenceOnboardingSite(normalizedDomain)) as IResolvedOnboardingSite;

                        setCurrentStep(OnboardingProgressStep.SettingUpSpace);
                        const syncCoreSiteId = await importSiteIntoSyncCore(apiContext.api, site.uuid as Uuid);

                        setCurrentStep(OnboardingProgressStep.CreatingOnboardingTask);
                        let taskGroup = await findLatestOnboardingTaskGroup(apiContext.api, site.uuid as Uuid);

                        if (!taskGroup) {
                          setCurrentStep(OnboardingProgressStep.InitializingOnboarding);
                          await initiateOnboardingTaskGroups(apiContext.api, syncCoreSiteId);

                          for (let attempt = 0; attempt < 15 && !taskGroup; attempt += 1) {
                            taskGroup = await findLatestOnboardingTaskGroup(apiContext.api, site.uuid as Uuid);
                            if (!taskGroup) {
                              await sleep(1_500);
                            }
                          }
                        }

                        if (!taskGroup) {
                          throw new Error('Sorry, onboarding task creation failed. Please try refreshing the page.');
                        }

                        const taskGroupId = String(taskGroup.id as InternalId);

                        for (let attempt = 0; attempt < 120; attempt += 1) {
                          const activeStep = await resolveActiveOnboardingProgressStep(apiContext.api, taskGroupId as InternalId);
                          setCurrentStep(activeStep);

                          if (activeStep === OnboardingProgressStep.Done) {
                            break;
                          }

                          await sleep(1_500);
                        }

                        sendToParent({
                          type: 'update-path',
                          path: `/brand-presence/onboarding/${encodeURIComponent(taskGroupId)}/key-content`,
                        });
                        navigate(`/brand-presence/onboarding/${encodeURIComponent(taskGroupId)}/key-content`);
                      } catch (submitError) {
                        setError((submitError as Error)?.message || 'Failed to initialize governance onboarding.');
                      } finally {
                        setResolving(false);
                      }
                    }}
                  >
                    {() => (
                      <div className="space-y-4">
                        <div className="relative rounded-xl border border-slate-200 bg-white shadow-sm transition-all duration-150 focus-within:border-primary focus-within:ring-4 focus-within:ring-primary/10">
                          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden>
                            <FontAwesomeIcon icon={faGlobe} />
                          </div>
                          <div className="pl-12 pr-32">
                            <Field name={'domain'}>
                              {({ field, meta, form }: FieldProps<string>) => (
                                <>
                                  <input
                                    {...field}
                                    type={'text'}
                                    placeholder={'example.com'}
                                    onBlur={(event) => {
                                      const normalizedDomain = normalizeDomainInput(event.target.value);
                                      form.setFieldValue('domain', normalizedDomain, false);
                                      form.setFieldTouched('domain', true, true);
                                    }}
                                    className="h-14 w-full border-0 bg-transparent px-0 text-base text-slate-900 placeholder:text-slate-400 focus:border-0 focus:ring-0"
                                  />
                                  {meta.touched && meta.error ? <p className="mt-2 text-sm text-danger">{meta.error}</p> : null}
                                </>
                              )}
                            </Field>
                          </div>
                          <button
                            type="submit"
                            disabled={resolving}
                            className="absolute right-1 top-1 bottom-1 rounded-md px-6 bg-brand-gradient font-semibold text-white shadow-sm transition-all duration-150 hover:shadow-card-hover hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {resolving ? 'Analyzing…' : 'Analyze'}
                          </button>
                        </div>

                        {error ? <p className="text-sm text-danger">{error}</p> : null}
                      </div>
                    )}
                  </GovernanceOnboardingForm>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </GovernanceOnboardingContainer>
  );
}
